import { CircleDollarSign, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { GroupStatsSummary } from '@/types/reports';
import { ENERGY_RATES } from '@/lib/energyConstants';
import { currentSupabaseEnv } from '@/lib/supabase';

interface GridRevenueCardProps {
  stats: GroupStatsSummary | null;
  title?: string;
  /** Optional dynamic rate - grid sell rate $/kWh (falls back to system default) */
  gridSellRate?: number;
  /** Optional dynamic rate - grid import rate $/kWh (falls back to system default) */
  gridImportRate?: number;
}

export function GridRevenueCard({
  stats,
  title = 'Grid Revenue',
  gridSellRate,
  gridImportRate,
}: GridRevenueCardProps) {
  // Use dynamic rates if provided, fall back to system defaults
  const effectiveSellRate = gridSellRate ?? ENERGY_RATES.GRID_SELL_RATE_PER_KWH;
  const effectiveImportRate = gridImportRate ?? ENERGY_RATES.GRID_IMPORT_RATE_PER_KWH;
  // Empty state - maintains same layout structure to prevent shift
  if (!stats) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="pb-2 flex-shrink-0">
          <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <CircleDollarSign className="h-4 w-4 text-[#8FB560] opacity-50" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col">
          {/* Hero placeholder */}
          <div className="text-center pb-3">
            <div className="text-3xl font-bold text-muted-foreground/30 tabular-nums">--</div>
            <div className="text-xs text-muted-foreground/50 mt-1">Total Benefit</div>
          </div>
          {/* Treemap placeholder */}
          <div className="py-3 flex-1 min-h-[100px]">
            <div className="h-full rounded-lg overflow-hidden bg-muted/10 flex items-center justify-center">
              <span className="text-sm text-muted-foreground/50">Select a group</span>
            </div>
          </div>
          {/* Bottom stats placeholder */}
          <div className="mt-auto pt-3 border-t border-border/50">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-xs text-muted-foreground/50">Exported</div>
                <div className="text-sm font-medium text-muted-foreground/30">--</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground/50">Rate</div>
                <div className="text-sm font-medium text-muted-foreground/30">--</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground/50">Revenue</div>
                <div className="text-sm font-medium text-muted-foreground/30">--</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const gridExportKwh = stats.totalGridExportKwh ?? 0;
  const gridImportKwh = stats.totalGridImportKwh ?? stats.totalGridKwh ?? 0;
  // The DB's revenue figure is authoritative on prod — 0 means 0; deriving
  // it from export kWh at a default rate invented dollars for groups whose
  // real buyback revenue is genuinely zero. The demo environment keeps its
  // simulated revenue fallback.
  const gridSellRevenue = currentSupabaseEnv === 'demo'
    ? (stats.gridSellRevenue || Math.round(gridExportKwh * effectiveSellRate))
    : (stats.gridSellRevenue ?? 0);
  const costSaved = stats.totalCostSaved;
  const gridCost = Math.round(gridImportKwh * effectiveImportRate);

  // Total Value = Savings + Grid Revenue - Grid Cost
  const totalValue = costSaved + gridSellRevenue - gridCost;

  // Calculate percentages for treemap visualization (based on absolute values for visual sizing)
  const totalAbsolute = costSaved + gridSellRevenue + gridCost;
  const savingsPercent = totalAbsolute > 0 ? Math.round((costSaved / totalAbsolute) * 100) : 0;
  const gridSellPercent = totalAbsolute > 0 ? Math.round((gridSellRevenue / totalAbsolute) * 100) : 0;
  const gridCostPercent = totalAbsolute > 0 ? 100 - savingsPercent - gridSellPercent : 0;

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <CircleDollarSign className="h-4 w-4 text-[#8FB560]" />
            {title}
          </CardTitle>
          {/* Info popover at top right of card */}
          <Popover>
            <PopoverTrigger asChild>
              <button className="p-1 rounded-full hover:bg-muted transition-colors">
                <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground transition-colors" />
              </button>
            </PopoverTrigger>
            <PopoverContent side="left" align="start" className="w-56 p-3">
              <div className="text-sm space-y-1.5">
                <div className="font-medium text-foreground border-b border-border pb-1.5 mb-1.5">Total Value Calculation</div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Solar Savings:</span>
                  <span className="text-green-600 dark:text-green-400 font-medium">+${costSaved.toLocaleString()}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Grid Revenue:</span>
                  <span className="text-amber-600 dark:text-amber-400 font-medium">+${Math.round(gridSellRevenue).toLocaleString()}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Grid Cost:</span>
                  <span className="text-red-600 dark:text-red-400 font-medium">-${gridCost.toLocaleString()}</span>
                </div>
                <div className="flex justify-between gap-4 border-t border-border pt-1.5 mt-1.5 font-medium">
                  <span className="text-foreground">Total Value:</span>
                  <span className="text-green-600 dark:text-green-400">${Math.round(totalValue).toLocaleString()}</span>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col">
        {/* Hero Total Value */}
        <div className="text-center pb-3">
          <div className="text-3xl font-bold text-[#8FB560] tabular-nums">
            ${Math.round(totalValue).toLocaleString()}
          </div>
          <div className="text-xs text-muted-foreground mt-1">Total Value</div>
        </div>

        {/* Treemap Chart */}
        <div className="py-3 flex-1 min-h-[100px]">
          <div className="h-full rounded-lg overflow-hidden bg-muted/10 flex gap-0.5 p-0.5">
            {/* Cost Savings - Green block (+) */}
            <div
              className="bg-gradient-to-br from-[#8FB560] to-[#7a9c4d] rounded-md flex flex-col items-center justify-center transition-all duration-500 relative isolate group hover:from-[#a4c574] hover:to-[#8FB560]"
              style={{ width: `${savingsPercent}%`, minWidth: savingsPercent > 0 ? '50px' : '0' }}
            >
              {savingsPercent > 0 && (
                <>
                  <span className="text-lg font-bold text-white drop-shadow-md relative z-10">
                    ${costSaved.toLocaleString()}
                  </span>
                  <span className="text-[10px] text-white/80 uppercase tracking-wide mt-0.5 relative z-10">
                    Savings
                  </span>
                </>
              )}
            </div>
            {/* Grid Revenue - Amber block (+) */}
            <div
              className="bg-gradient-to-br from-amber-600 to-amber-700 rounded-md flex flex-col items-center justify-center transition-all duration-500 relative isolate group hover:from-amber-500 hover:to-amber-600"
              style={{ width: `${gridSellPercent}%`, minWidth: gridSellPercent > 0 ? '50px' : '0' }}
            >
              {gridSellPercent > 0 && (
                <>
                  <span className="text-lg font-bold text-white drop-shadow-md relative z-10">
                    ${Math.round(gridSellRevenue).toLocaleString()}
                  </span>
                  <span className="text-[10px] text-white/80 uppercase tracking-wide mt-0.5 relative z-10">
                    Revenue
                  </span>
                </>
              )}
            </div>
            {/* Grid Cost - Red block (-) */}
            <div
              className="bg-gradient-to-br from-red-600 to-red-700 rounded-md flex flex-col items-center justify-center transition-all duration-500 relative isolate group hover:from-red-500 hover:to-red-600"
              style={{ width: `${gridCostPercent}%`, minWidth: gridCostPercent > 0 ? '50px' : '0' }}
            >
              {gridCostPercent > 0 && (
                <>
                  <span className="text-lg font-bold text-white drop-shadow-md relative z-10">
                    -${gridCost.toLocaleString()}
                  </span>
                  <span className="text-[10px] text-white/80 uppercase tracking-wide mt-0.5 relative z-10">
                    Cost
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Grid Export Details - Compact at bottom */}
        <div className="mt-auto pt-3 border-t border-border/50">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="text-xs text-muted-foreground">Exported</div>
              <div className="text-sm font-medium tabular-nums">
                {gridExportKwh >= 1_000_000 ? `${(gridExportKwh / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} GWh` : `${Math.round(gridExportKwh).toLocaleString()} kWh`}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Rate</div>
              <div className="text-sm font-medium tabular-nums">${effectiveSellRate.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Revenue</div>
              <div className="text-sm font-medium tabular-nums text-amber-500">${Math.round(gridSellRevenue).toLocaleString()}</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
